/**
 * Calendar-date arithmetic on `YYYY-MM-DD` strings (database `date`). Pure and timezone-free:
 * a date is a day, not an instant. "Today" in the business timezone comes from `todayIso()`
 * in format.ts (the only module allowed to use Intl).
 */

const DAY_MS = 86_400_000;

const toUtc = (iso: string): number => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
};

const fromUtc = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export function addDays(iso: string, days: number): string {
  return fromUtc(toUtc(iso) + days * DAY_MS);
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round((toUtc(to) - toUtc(from)) / DAY_MS);
}

/** Monday of the ISO week containing `iso`. */
export function startOfWeek(iso: string): string {
  const weekday = (new Date(toUtc(iso)).getUTCDay() + 6) % 7; // Monday = 0
  return addDays(iso, -weekday);
}

/** Sunday of the ISO week containing `iso`. */
export function endOfWeek(iso: string): string {
  return addDays(startOfWeek(iso), 6);
}

/** Chronological comparison; `null` sorts first ("empty = earliest", FR-DASH-007). */
export function compareDates(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return a < b ? -1 : 1;
}

/** Like compareDates, but `null` sorts last (deadlines: "without deadline — at the end"). */
export function compareDatesNullsLast(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return compareDates(a, b);
}

/** ISO 8601 week of a date: the week's year (may differ at year ends) and number 1–53. */
export function isoWeek(iso: string): { year: number; week: number } {
  const thursday = addDays(startOfWeek(iso), 3);
  const year = Number(thursday.slice(0, 4));
  return { year, week: Math.floor(daysBetween(`${year}-01-01`, thursday) / 7) + 1 };
}
